import { withStyles, Card, InterestAccount, InterestTerm, BodyText, Tooltip, formatAmount, Box } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';
import useFetchInterestAccount from '@/hooks/useApi/useFetchInterestAccounts';
import useFetchInterestAccountRates from '@/hooks/useApi/useFetchInterestAccountRates';
import BalanceListItem from './BalanceListItem/BalanceListItem';
import BalanceDetails from './BalanceDetails/BalanceDetails';
import UpgradePlanModal from './AccountUI/Modal/UpgradePlanModal';
import InterestAccountFundTransferModal from './AccountUI/Modal/InterestAccountFundTransferModal';
import styles from './AccountSummaryPage.module.scss';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

export type AccountType = 'disabled' | 'standard' | 'premium';
export type TransferType = 'Fund' | 'Withdraw';

const cn = withStyles(styles);

const AccountSummaryPage: FunctionComponent = () => {
  const alertDialog = useAlertDialog();
  const { permissions } = usePermissions();
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const { balances } = useAccountBalances();
  const [selectedBalance, setSelectedBalance] = useState<Balance>();
  const [showingUpgradePlanModal, setShowingUpgradePlanModal] = useState(false);
  const [showingFundTransferModal, setIsShowingFundTransferModal] = useState(false);
  const [transferType, setTransferType] = useState<TransferType>('Fund');
  const { fetchInterestAccounts } = useFetchInterestAccount();
  const { interestRates, fetchInterestRates } = useFetchInterestAccountRates();
  const { SubAccountPermissions } = useLockedFeatures();

  const interestAccountEnabled = useMemo(() => {
    if (permissions.ViewInterestAccounts && response.InterestAccountEnabled) {
      return true;
    }
    return false;
  }, [permissions, response]);

  const defaultAccount = {
    AccountName: '',
    CurrentBalance: '0.00',
    InterestAccrued: '0.00',
    EligibleBalance: '0.00',
    InterestMaturityType: '',
  };

  const [shortTermAccount, setShortTermAccount] = useState(defaultAccount);
  const [longTermAccount, setLongTermAccount] = useState<InterestAccount | null>(null);

  const getInterestAccountRates = async () => {
    if (interestAccountEnabled) {
      const interestRatesResponse = await fetchInterestRates({});
      if (!interestRatesResponse.Success) {
        alertDialog.error('Error', interestRatesResponse.ErrorMessage);
      }
    }
  };

  const getInterestAccounts = async () => {
    const interestResponse = await fetchInterestAccounts({});

    if (!interestResponse.Success) {
      alertDialog.error('Error', interestResponse.ErrorMessage);
    } else {
      setShortTermAccount(getAccountByMaturityType(interestResponse.InterestAccounts, 'short-term') || defaultAccount);
      setLongTermAccount(getAccountByMaturityType(interestResponse.InterestAccounts, 'long-term') || null);
    }
  };

  useEffect(() => {
    if (interestAccountEnabled) {
      getInterestAccounts();
    }
    // if the permission is disabled, only returns the interest rate for the negative balance
    getInterestAccountRates();
  }, [interestAccountEnabled]);

  useEffect(() => {
    setSelectedBalance(balances.find((b) => b.Currency === selectedBalance?.Currency) || balances[0]);
  }, [balances]);

  return (
    <div className={cn('accountSummaryPage')}>
      <header className={cn('header')}>
        <Title>{t('title')}</Title>
      </header>

      <Card
        style={{
          padding: '0px',
          dropShadow: '0px 10px 12px #2D2D2D1A',
          borderColor: '#2D2D2D1A',
          backgroundColor: '#fbfbfb',
        }}
      >
        <div className={cn('accountSummaryContainer')}>
          <section className={cn('left')} aria-label="balances">
            <ul className={cn('balances', 'fade', 'scrollbar')}>
              {balances.map((b) => (
                <li key={b.Currency} onClick={() => setSelectedBalance(b)}>
                  <BalanceListItem
                    accountType={b.Currency === 'CAD' ? getInterestAccountType() : 'disabled'}
                    balance={b}
                    selected={selectedBalance?.Currency === b.Currency}
                    onOpenModal={() => {
                      setShowingUpgradePlanModal(true);
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>

          {selectedBalance && (
            <section className={cn('right')} aria-label={`details for ${selectedBalance.Currency}`}>
              <Card
                className={cn('detailsCard')}
                style={{
                  padding: '0px',
                  dropShadow: '0px 10px 12px #2D2D2D1A',
                  borderColor: '#2D2D2D1A',
                  backgroundColor: '#fbfbfb',
                }}
              >
                <div className={cn('detailsContainer', 'scrollbar')}>
                  <BalanceDetails
                    accountType={selectedBalance.Currency === 'CAD' ? getInterestAccountType() : 'disabled'}
                    balance={selectedBalance}
                    shortTermAccount={shortTermAccount}
                    longTermAccount={longTermAccount}
                    interestRates={interestRates}
                    onOpenUpgradePlanModal={() => {
                      setShowingUpgradePlanModal(true);
                    }}
                    onOpenFundTransferModal={(type: TransferType) => {
                      setIsShowingFundTransferModal(true);
                      setTransferType(type);
                    }}
                  />
                  {!!SubAccountPermissions && (
                    <Card className={cn('subAccountCard')}>
                      <Title level="4" className={cn('subAccountTitle')}>
                        {t('SubAccount.title', {
                          defaultValue: 'Sub-Account Information',
                        })}
                      </Title>
                      <div className={cn('subAccountDetailsContainer')}>
                        <div className={cn('subAccountDetailsInfo')}>
                          <div className={cn('subAccountDetailsInfoHeader')}>
                            <BodyText weight="md" size="rg">
                              {t('SubAccount.rollUpBalance', {
                                defaultValue: 'Available Roll-Up Balance',
                              })}
                            </BodyText>
                            <Tooltip
                              icon="info"
                              position="right"
                              information={
                                <Box gapSize="8px" flx="col">
                                  <div className={cn('tooltipTitle')}>
                                    {t('SubAccount.rollUpBalance', {
                                      defaultValue: 'Available Roll-Up Balance',
                                    })}
                                  </div>
                                  <div className={cn('message')}>
                                    {t('SubAccount.rollUpBalanceTooltip', {
                                      defaultValue:
                                        'This balance is a visual summary of all sub-accounts, calculated as the total of their balances. Funds cannot be moved from this account.',
                                    })}
                                  </div>
                                </Box>
                              }
                            />
                          </div>
                          <BodyText weight="rg" size="sm" className={cn('subAccountDetailsInfoDescription')}>
                            {t('SubAccount.rollUpBalanceDescription', {
                              defaultValue: 'Funds rolled up from sub-accounts. Not available for use or transfer.',
                            })}
                          </BodyText>
                        </div>
                        <BodyText className={cn('subAccountDetailsInfoBalance')}>
                          {selectedBalance.Currency} {formatAmount(selectedBalance.RollupAvailableBalance || 0)}
                        </BodyText>
                      </div>
                    </Card>
                  )}
                </div>
              </Card>
            </section>
          )}
        </div>
      </Card>

      <UpgradePlanModal
        isVisible={showingUpgradePlanModal}
        accountType={selectedBalance?.Currency === 'CAD' ? getInterestAccountType() : 'disabled'}
        onCloseModal={() => {
          setShowingUpgradePlanModal(false);
        }}
      />

      <InterestAccountFundTransferModal
        transferType={transferType}
        visible={showingFundTransferModal}
        currentBalance={
          transferType === 'Fund' ? shortTermAccount.EligibleBalance : longTermAccount?.EligibleBalance || ''
        }
        onClose={(added) => {
          setIsShowingFundTransferModal(false);
          if (added) getInterestAccounts();
        }}
        currency={selectedBalance?.Currency || 'CAD'}
      />
    </div>
  );

  function getInterestAccountType() {
    if (!interestAccountEnabled) return 'disabled';
    if (longTermAccount) return 'premium';
    return 'standard';
  }

  function getAccountByMaturityType(accounts: InterestAccount[], maturityType: InterestTerm) {
    return accounts.find((account) => account.InterestMaturityType === maturityType);
  }
};

export default AccountSummaryPage;
